/**
 * @file   src\store\actions\reportAction.ts
 * @brief  This file is responsible for creating asynchronous api call for CA report slice.
 * @date   Oct, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../utils/apiConfig';
import {
  IAttendanceReportRequest,
  IAttendanceReportExportRequest,
  ICalloutReportRequest,
  ICalloutPdfReportRequest,
  IStaffSummaryPdfReportRequest,
  IStaffSummaryReportRequest,
  IShiftSummaryReportRequest,
  IShiftSummaryPdfReportRequest,
} from '../../interfaces/reportInterface';
import { ReportApi, KKIActionTypes } from '../../utils/apiUrls';
import { buildQueryString } from '../../utils/helper';

// Get CA attendance report api call.
export const getCaAttendanceReport = createAsyncThunk('/report/getCaAttendance', async (request: IAttendanceReportRequest, { rejectWithValue }) => {
  try {
    const { data } = await axios.get(`${ReportApi.GET_CA_ATTENDANCE}?${buildQueryString(request)}`);
    return data;
  } catch (error: any) {
    return rejectWithValue(error.data);
  }
});

// Export CA attendance as pdf api call.
export const exportAttendanceReportAsExcel = createAsyncThunk('/report/exportAttendanceReportAsExcel', async (request: IAttendanceReportExportRequest, { rejectWithValue }) => {
  try {
    const { data } = await axios.get(`${ReportApi.ATTENDANCE_REPORT_EXCEL_EXPORT}?${buildQueryString(request)}`, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    });
    return data;
  } catch (error: any) {
    return rejectWithValue(error.data);
  }
});

// Get callout report api call.
export const getCalloutReport = createAsyncThunk('/report/getCalloutReport', async (request: ICalloutReportRequest, { rejectWithValue }) => {
  try {
    const { data } = await axios.get(`${ReportApi.GET_CALLOUT_REPORT}?${buildQueryString(request)}`);
    return data;
  } catch (error: any) {
    return rejectWithValue(error.data);
  }
});

// Export callout report as pdf api call.
export const exportCalloutReportAsPdf = createAsyncThunk('/report/exportCalloutReportAsPdf', async (request: ICalloutPdfReportRequest, { rejectWithValue }) => {
  try {
    const { data } = await axios.get(`${ReportApi.CALLOUT_PDF_EXPORT}?${buildQueryString(request)}`, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/pdf', // adjust based on API requirements
      },
    });
    return data;
  } catch (error: any) {
    return rejectWithValue(error.data);
  }
});

// Get staff summary report api call.
export const getStaffSummaryReport = createAsyncThunk('/report/getStaffSummaryReport', async (request: IStaffSummaryReportRequest, { rejectWithValue }) => {
  try {
    const { data } = await axios.get(`${ReportApi.GET_STAFF_SUMMARY_REPORT}?${buildQueryString(request)}`);
    return data;
  } catch (error: any) {
    return rejectWithValue(error.data);
  }
});

// Export staff summary report as pdf api call.
export const exportStaffSummaryReportAsPdf = createAsyncThunk('/report/exportStaffSummaryReportAsPdf', async (request: IStaffSummaryPdfReportRequest, { rejectWithValue }) => {
  try {
    const { data } = await axios.get(`${ReportApi.STAFF_SUMMARY_PDF_EXPORT}?${buildQueryString(request)}`, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/pdf', // adjust based on API requirements
      },
    });
    return data;
  } catch (error: any) {
    return rejectWithValue(error.data);
  }
});

// Get shift summary report api call.
export const getShiftSummaryReport = createAsyncThunk('/report/getShiftSummaryReport', async (request: IShiftSummaryReportRequest, { rejectWithValue }) => {
  try {
    const { data } = await axios.get(`${ReportApi.GET_SHIFT_SUMMARY_REPORT}?${buildQueryString(request)}`);
    return data;
  } catch (error: any) {
    return rejectWithValue(error.data);
  }
});

// Export shift summary report as pdf api call.
export const exportShiftSummaryReportAsPdf = createAsyncThunk('/report/exportShiftSummaryReportAsPdf', async (request: IShiftSummaryPdfReportRequest, { rejectWithValue }) => {
  try {
    const { data } = await axios.get(`${ReportApi.SHIFT_SUMMARY_PDF_EXPORT}?${buildQueryString(request)}`, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/pdf', // adjust based on API requirements
      },
    });
    return data;
  } catch (error: any) {
    return rejectWithValue(error.data);
  }
});

// Export callout report as excel api call.
export const exportCalloutReportAsExcel = createAsyncThunk('/report/exportCalloutReportAsExcel', async (request: ICalloutPdfReportRequest, { rejectWithValue }) => {
  try {
    const { data } = await axios.get(`${ReportApi.CALLOUT_EXCEL_EXPORT}?${buildQueryString(request)}`, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // adjust based on API requirements
      },
    });
    return data;
  } catch (error: any) {
    return rejectWithValue(error.data);
  }
});

// Export staff summary report as excel api call.
export const exportStaffSummaryReportAsExcel = createAsyncThunk('/report/exportStaffSummaryReportAsExcel', async (request: IStaffSummaryPdfReportRequest, { rejectWithValue }) => {
  try {
    const { data } = await axios.get(`${ReportApi.STAFF_SUMMARY_EXCEL_EXPORT}?${buildQueryString(request)}`, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // adjust based on API requirements
      },
    });
    return data;
  } catch (error: any) {
    return rejectWithValue(error.data);
  }
});

// Export shift summary report as pdf api call.
export const exportShiftSummaryReportAsExcel = createAsyncThunk('/report/exportShiftSummaryReportAsExcel', async (request: IShiftSummaryPdfReportRequest, { rejectWithValue }) => {
  try {
    const { data } = await axios.get(`${ReportApi.SHIFT_SUMMARY_EXCEL_EXPORT}?${buildQueryString(request)}`, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // adjust based on API requirements
      },
    });
    return data;
  } catch (error: any) {
    return rejectWithValue(error.data);
  }
});

// Clear all report states to initial state action.
export const revertAllReportState = createAction(KKIActionTypes.REVERT_ALL_REPORT_STATE);
